import { SEARCH_TYPE } from '@/constants/searchType';

export const SITE_NAME = 'BreedLOGIC';

/**
 * Human-readable label for each society public-search section. Shared by the
 * server `generateMetadata` (per-page) and the client document-title logic so
 * the two never drift.
 */
export const SECTION_LABEL = {
  animals: 'Animal Search',
  animalDetail: 'Animal Information',
  mating: 'Mating Predictor',
  ebvPercentiles: 'EBV Percentiles',
  members: 'Member Search',
  memberDetail: 'Member Information',
  sales: 'Sale Catalogues',
  saleDetail: 'Sale Catalogue',
  saleNotes: 'Sale Notes',
  downloads: 'Downloads',
  registrations: 'Online Registrations',
  linkI4: 'Link i4 Account',
} as const;

const EBV_PERCENTAGES_PATH = 'ebv-percentages';
const LINK_I4_PATH = 'link-i4';

/**
 * Maps a society public-search pathname (e.g. `/ABBA/animals/2741396`) to its
 * page label. Returns undefined for the bare `/{code}` landing and any route
 * not under a known section, so the title falls back to just the society name.
 */
export function resolveSocietySectionLabel(
  pathname: string | null | undefined,
): string | undefined {
  if (!pathname) return undefined;
  const tail = pathname.match(/^\/[^/]+\/(.+)$/)?.[1] ?? '';
  const [section, sub, subSub] = tail.split('/').filter(Boolean);
  if (!section) return undefined;

  if (section === 'animals') {
    if (!sub) return SECTION_LABEL.animals;
    if (sub === SEARCH_TYPE.MATING) return SECTION_LABEL.mating;
    if (sub === EBV_PERCENTAGES_PATH) return SECTION_LABEL.ebvPercentiles;
    return SECTION_LABEL.animalDetail;
  }
  if (section === 'members') {
    return sub ? SECTION_LABEL.memberDetail : SECTION_LABEL.members;
  }
  if (section === 'sales') {
    if (!sub) return SECTION_LABEL.sales;
    if (subSub === 'notes') return SECTION_LABEL.saleNotes;
    return SECTION_LABEL.saleDetail;
  }
  if (section === 'external') {
    if (sub === 'registrations') return SECTION_LABEL.registrations;
    if (sub === SEARCH_TYPE.DOWNLOADS) return SECTION_LABEL.downloads;
    if (sub === LINK_I4_PATH) return SECTION_LABEL.linkI4;
  }
  return undefined;
}

/**
 * Joins the available parts into the `{page} | {society} | BreedLOGIC` title,
 * dropping any empty segment (e.g. no section on the bare landing, or an
 * unresolved society name).
 */
export function buildPageTitle(parts: {
  label?: string;
  societyName?: string;
}): string {
  return [parts.label, parts.societyName, SITE_NAME]
    .map((segment) => segment?.trim())
    .filter((segment): segment is string => Boolean(segment))
    .join(' | ');
}
